import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { FIRE_COLORS, getBlockColor } from '../BlazeMaterial';

// Easing functions
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);
const easeOutExpo = (t) => t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
const easeInQuad = (t) => t * t;
const easeInCubic = (t) => t * t * t;
const easeInOutQuad = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
const easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const easeOutBack = (t) => { const c1 = 1.70158; const c3 = c1 + 1; return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2); };
const smoothstep = (t) => t * t * (3 - 2 * t);

// Tetromino shapes for the falling tetromino rain effect
const TETROMINO_SHAPES = [
  // T-piece
  [[0, 0], [1, 0], [2, 0], [1, 1]],
  // L-piece
  [[0, 0], [0, 1], [0, 2], [1, 2]],
  // S-piece
  [[1, 0], [2, 0], [0, 1], [1, 1]],
  // Z-piece
  [[0, 0], [1, 0], [1, 1], [2, 1]],
  // I-piece
  [[0, 0], [1, 0], [2, 0], [3, 0]],
  // O-piece
  [[0, 0], [1, 0], [0, 1], [1, 1]],
];

// --- MOLTEN SHATTER BLOCK: Block heats up, cracks into fragments that fly outward with lava trails ---
const MoltenShatterBlock = ({ position, color, delay, col, row, centerX, centerY }) => {
  const blockRef = useRef();
  const crackGlowRef = useRef();
  const haloRef = useRef();
  const fragment1Ref = useRef();
  const fragment2Ref = useRef();
  const fragment3Ref = useRef();
  const trail1Ref = useRef();
  const trail2Ref = useRef();
  const trail3Ref = useRef();
  const startTime = useRef(null);
  const fragmentVelocities = useRef(null);

  const distFromCenter = useMemo(() => {
    return Math.sqrt((position[0] - centerX) ** 2 + (position[1] - centerY) ** 2);
  }, [position, centerX, centerY]);

  // Pre-compute fragment trajectories
  const fragData = useMemo(() => {
    const dirX = position[0] - centerX;
    const dirY = position[1] - centerY;
    const dist = Math.max(Math.sqrt(dirX * dirX + dirY * dirY), 0.5);
    const normX = dirX / dist;
    const normY = dirY / dist;
    const baseAngle = Math.atan2(normY, normX);

    return [0, 1, 2].map((i) => {
      const spreadAngle = baseAngle + (i - 1) * 0.6 + (Math.random() - 0.5) * 0.3;
      const speed = 3.5 + Math.random() * 2.5 + distFromCenter * 0.4;
      return {
        vx: Math.cos(spreadAngle) * speed,
        vy: Math.sin(spreadAngle) * speed + 2.5 + Math.random() * 2,
        spin: (Math.random() - 0.5) * 20,
        spinX: (Math.random() - 0.5) * 12,
        scale: 0.3 + Math.random() * 0.25,
      };
    });
  }, [position, centerX, centerY, distFromCenter]);

  useFrame((state) => {
    if (!blockRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0) {
      blockRef.current.visible = false;
      if (crackGlowRef.current) crackGlowRef.current.visible = false;
      if (haloRef.current) haloRef.current.visible = false;
      [fragment1Ref, fragment2Ref, fragment3Ref].forEach(r => { if (r.current) r.current.visible = false; });
      [trail1Ref, trail2Ref, trail3Ref].forEach(r => { if (r.current) r.current.visible = false; });
      return;
    }

    if (adjustedTime > 2.0) {
      blockRef.current.visible = false;
      if (crackGlowRef.current) crackGlowRef.current.visible = false;
      if (haloRef.current) haloRef.current.visible = false;
      [fragment1Ref, fragment2Ref, fragment3Ref].forEach(r => { if (r.current) r.current.visible = false; });
      [trail1Ref, trail2Ref, trail3Ref].forEach(r => { if (r.current) r.current.visible = false; });
      return;
    }

    const flicker = Math.sin(elapsed * 50 + col * 7 + row * 3) * 0.3 + 0.7;
    const crackFlicker = Math.sin(elapsed * 80 + col * 13) * 0.4 + 0.6;

    if (adjustedTime < 0.35) {
      // Phase 1: MAGMA BUILDUP — intense heat, lava cracks widen, block swells
      blockRef.current.visible = true;
      const heatProgress = adjustedTime / 0.35;
      const eased = easeInCubic(heatProgress);

      // Block pulses with increasing intensity
      const pulse = Math.sin(heatProgress * Math.PI * 6) * 0.04 * eased;
      blockRef.current.material.emissiveIntensity = 1.5 + eased * 12 * flicker;
      blockRef.current.material.opacity = 1;
      blockRef.current.scale.setScalar(1 + eased * 0.12 + pulse);
      blockRef.current.position.set(
        position[0] + Math.sin(elapsed * 60 + col) * eased * 0.03,
        position[1] + Math.cos(elapsed * 55 + row) * eased * 0.03,
        position[2]
      );

      // Lava crack glow intensifies with pulsing
      if (crackGlowRef.current) {
        crackGlowRef.current.visible = heatProgress > 0.15;
        const crackIntensity = Math.max(0, (heatProgress - 0.15) / 0.85);
        crackGlowRef.current.material.opacity = crackIntensity * 0.85 * crackFlicker;
        crackGlowRef.current.scale.setScalar(0.92 + crackIntensity * 0.16);
        crackGlowRef.current.position.set(position[0], position[1], position[2] + 0.08);
      }

      // Heat halo blooms
      if (haloRef.current) {
        haloRef.current.visible = heatProgress > 0.4;
        const haloIntensity = Math.max(0, (heatProgress - 0.4) / 0.6);
        haloRef.current.material.opacity = haloIntensity * 0.4 * flicker;
        haloRef.current.scale.setScalar(1.3 + eased * 0.5);
        haloRef.current.position.set(position[0], position[1], position[2] - 0.05);
      }

      // Hide fragments during buildup
      [fragment1Ref, fragment2Ref, fragment3Ref].forEach(r => { if (r.current) r.current.visible = false; });
      [trail1Ref, trail2Ref, trail3Ref].forEach(r => { if (r.current) r.current.visible = false; });

    } else if (adjustedTime < 0.75) {
      // Phase 2: SHATTER & ERUPT — block shatters into 3 molten fragments flying outward
      blockRef.current.visible = false;
      if (crackGlowRef.current) crackGlowRef.current.visible = false;

      const shatterProgress = (adjustedTime - 0.35) / 0.4;
      const eased = easeOutBack(Math.min(shatterProgress, 1));
      const gravity = -8;
      const t = adjustedTime - 0.35;

      const fragRefs = [fragment1Ref, fragment2Ref, fragment3Ref];
      const trailRefs = [trail1Ref, trail2Ref, trail3Ref];

      fragRefs.forEach((ref, i) => {
        if (!ref.current) return;
        ref.current.visible = true;

        const fd = fragData[i];
        const fx = position[0] + fd.vx * t;
        const fy = position[1] + fd.vy * t + 0.5 * gravity * t * t;
        const fz = position[2] + 0.1;

        ref.current.position.set(fx, fy, fz);
        ref.current.rotation.z += fd.spin * 0.016;
        ref.current.rotation.x += fd.spinX * 0.016;

        // White-hot → orange → red color temperature shift
        const tempShift = shatterProgress;
        ref.current.material.emissiveIntensity = (18 - tempShift * 10) * flicker;
        ref.current.material.opacity = 1 - shatterProgress * 0.3;
        ref.current.scale.setScalar(fd.scale * (1 - shatterProgress * 0.2));

        // Lava trail behind each fragment
        if (trailRefs[i].current) {
          trailRefs[i].current.visible = true;
          // Trail sits slightly behind the fragment
          const trailT = Math.max(0, t - 0.02);
          const tx = position[0] + fd.vx * trailT;
          const ty = position[1] + fd.vy * trailT + 0.5 * gravity * trailT * trailT;
          trailRefs[i].current.position.set(tx, ty, fz - 0.03);
          trailRefs[i].current.material.opacity = 0.6 * (1 - shatterProgress * 0.5) * crackFlicker;
          trailRefs[i].current.scale.set(fd.scale * 1.4, fd.scale * 2.2, 1);
          // Orient trail along velocity direction
          const velAngle = Math.atan2(fd.vy + gravity * t, fd.vx);
          trailRefs[i].current.rotation.z = velAngle - Math.PI / 2;
        }
      });

      // Halo expands and fades
      if (haloRef.current) {
        haloRef.current.visible = true;
        haloRef.current.position.set(position[0], position[1], position[2] - 0.05);
        haloRef.current.material.opacity = 0.5 * (1 - shatterProgress);
        haloRef.current.scale.setScalar(1.8 + eased * 1.5);
      }

    } else {
      // Phase 3: DISSOLVE — fragments shrink and fade like dying embers
      blockRef.current.visible = false;
      if (crackGlowRef.current) crackGlowRef.current.visible = false;
      if (haloRef.current) haloRef.current.visible = false;

      const fadeProgress = (adjustedTime - 0.75) / 1.25;
      const eased = easeOutQuint(fadeProgress);
      const gravity = -8;
      const t = adjustedTime - 0.35;

      const fragRefs = [fragment1Ref, fragment2Ref, fragment3Ref];
      const trailRefs = [trail1Ref, trail2Ref, trail3Ref];

      fragRefs.forEach((ref, i) => {
        if (!ref.current) return;
        const fd = fragData[i];
        const fx = position[0] + fd.vx * t;
        const fy = position[1] + fd.vy * t + 0.5 * gravity * t * t;
        ref.current.position.set(fx, fy, position[2] + 0.1);

        // Slow down spinning
        ref.current.rotation.z += fd.spin * 0.016 * (1 - eased);
        ref.current.rotation.x += fd.spinX * 0.016 * (1 - eased);

        // Ember fade — pulsing dimly before dying
        const emberPulse = Math.sin(adjustedTime * 15 + i * 2) * 0.3 + 0.7;
        ref.current.material.emissiveIntensity = 6 * (1 - eased) * emberPulse;
        ref.current.material.opacity = Math.max(0, 0.7 * (1 - eased));
        ref.current.scale.setScalar(Math.max(0, fd.scale * (0.8 - eased * 0.8)));

        if (ref.current.scale.x <= 0.01) ref.current.visible = false;

        if (trailRefs[i].current) {
          trailRefs[i].current.material.opacity = Math.max(0, 0.3 * (1 - eased * 1.3));
          if (trailRefs[i].current.material.opacity <= 0.01) trailRefs[i].current.visible = false;
        }
      });
    }
  });

  return (
    <group>
      {/* Main block (pre-shatter) */}
      <mesh ref={blockRef} position={position} visible={false}>
        <boxGeometry args={[0.9, 0.9, 0.4]} />
        <meshStandardMaterial
          color={color}
          emissive={FIRE_COLORS.flameCore}
          emissiveIntensity={1.5}
          transparent
          opacity={1}
          metalness={0.5}
          roughness={0.25}
        />
      </mesh>

      {/* Lava crack glow overlay */}
      <mesh ref={crackGlowRef} position={[position[0], position[1], position[2] + 0.08]} visible={false}>
        <planeGeometry args={[0.88, 0.88]} />
        <meshBasicMaterial
          color={FIRE_COLORS.flameCore}
          transparent
          opacity={0}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Heat halo */}
      <mesh ref={haloRef} position={[position[0], position[1], position[2] - 0.05]} visible={false}>
        <circleGeometry args={[0.9, 20]} />
        <meshBasicMaterial
          color={FIRE_COLORS.flame}
          transparent
          opacity={0}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Fragment 1 */}
      <mesh ref={fragment1Ref} visible={false}>
        <boxGeometry args={[0.42, 0.42, 0.35]} />
        <meshStandardMaterial
          color={FIRE_COLORS.flameCore}
          emissive={FIRE_COLORS.coreWhite}
          emissiveIntensity={15}
          transparent
          metalness={0.6}
          roughness={0.2}
        />
      </mesh>
      {/* Fragment 2 */}
      <mesh ref={fragment2Ref} visible={false}>
        <boxGeometry args={[0.38, 0.38, 0.3]} />
        <meshStandardMaterial
          color={FIRE_COLORS.flame}
          emissive={FIRE_COLORS.flameCore}
          emissiveIntensity={15}
          transparent
          metalness={0.6}
          roughness={0.2}
        />
      </mesh>
      {/* Fragment 3 */}
      <mesh ref={fragment3Ref} visible={false}>
        <boxGeometry args={[0.35, 0.45, 0.28]} />
        <meshStandardMaterial
          color={FIRE_COLORS.ember}
          emissive={FIRE_COLORS.flame}
          emissiveIntensity={15}
          transparent
          metalness={0.5}
          roughness={0.25}
        />
      </mesh>

      {/* Lava trails */}
      {[trail1Ref, trail2Ref, trail3Ref].map((ref, i) => (
        <mesh key={`trail-${i}`} ref={ref} visible={false}>
          <planeGeometry args={[0.2, 0.5]} />
          <meshBasicMaterial
            color={i === 0 ? FIRE_COLORS.flameCore : FIRE_COLORS.flame}
            transparent
            opacity={0.6}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
};

// --- VOLCANIC CORE: Multi-layered eruption with color temperature rings ---
const VolcanicCore = ({ centerY }) => {
  const whiteRef = useRef();
  const yellowRef = useRef();
  const orangeRef = useRef();
  const redRef = useRef();
  const flashRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - 0.28;

    // White-hot innermost core
    if (whiteRef.current) {
      if (adjustedTime > 0 && adjustedTime < 0.6) {
        whiteRef.current.visible = true;
        const p = adjustedTime / 0.6;
        const scale = 0.3 + easeOutExpo(p) * 6;
        whiteRef.current.scale.setScalar(scale);
        const intensity = p < 0.15 ? easeInQuad(p / 0.15) : Math.pow(1 - (p - 0.15) / 0.85, 1.5);
        whiteRef.current.material.opacity = 0.95 * intensity;
      } else {
        whiteRef.current.visible = false;
      }
    }

    // Yellow ring
    if (yellowRef.current) {
      if (adjustedTime > 0.03 && adjustedTime < 0.65) {
        yellowRef.current.visible = true;
        const p = (adjustedTime - 0.03) / 0.62;
        const scale = 0.8 + easeOutExpo(p) * 9;
        yellowRef.current.scale.setScalar(scale);
        const intensity = p < 0.12 ? p / 0.12 : Math.pow(1 - (p - 0.12) / 0.88, 1.3);
        yellowRef.current.material.opacity = 0.75 * intensity;
      } else {
        yellowRef.current.visible = false;
      }
    }

    // Orange ring
    if (orangeRef.current) {
      if (adjustedTime > 0.06 && adjustedTime < 0.7) {
        orangeRef.current.visible = true;
        const p = (adjustedTime - 0.06) / 0.64;
        const scale = 1.2 + easeOutExpo(p) * 13;
        orangeRef.current.scale.setScalar(scale);
        const intensity = p < 0.1 ? p / 0.1 : Math.pow(1 - (p - 0.1) / 0.9, 1.2);
        orangeRef.current.material.opacity = 0.55 * intensity;
      } else {
        orangeRef.current.visible = false;
      }
    }

    // Red outermost ring
    if (redRef.current) {
      if (adjustedTime > 0.09 && adjustedTime < 0.8) {
        redRef.current.visible = true;
        const p = (adjustedTime - 0.09) / 0.71;
        const scale = 1.5 + easeOutExpo(p) * 18;
        redRef.current.scale.setScalar(scale);
        const intensity = p < 0.08 ? p / 0.08 : Math.pow(1 - (p - 0.08) / 0.92, 1.1);
        redRef.current.material.opacity = 0.35 * intensity;
      } else {
        redRef.current.visible = false;
      }
    }

    // Screen-wide flash
    if (flashRef.current) {
      if (adjustedTime > 0 && adjustedTime < 0.15) {
        flashRef.current.visible = true;
        const p = adjustedTime / 0.15;
        const intensity = Math.sin(p * Math.PI);
        flashRef.current.material.opacity = 0.7 * intensity;
        flashRef.current.scale.setScalar(30 + p * 10);
      } else {
        flashRef.current.visible = false;
      }
    }
  });

  return (
    <>
      <mesh ref={whiteRef} position={[0, centerY, 0.55]} visible={false}>
        <circleGeometry args={[0.5, 32]} />
        <meshBasicMaterial color={FIRE_COLORS.coreWhite} transparent opacity={0.95} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
      <mesh ref={yellowRef} position={[0, centerY, 0.52]} visible={false}>
        <circleGeometry args={[0.5, 32]} />
        <meshBasicMaterial color={FIRE_COLORS.flameCore} transparent opacity={0.75} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
      <mesh ref={orangeRef} position={[0, centerY, 0.49]} visible={false}>
        <circleGeometry args={[0.5, 32]} />
        <meshBasicMaterial color={FIRE_COLORS.flame} transparent opacity={0.55} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
      <mesh ref={redRef} position={[0, centerY, 0.46]} visible={false}>
        <circleGeometry args={[0.5, 32]} />
        <meshBasicMaterial color={FIRE_COLORS.ember} transparent opacity={0.35} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
      <mesh ref={flashRef} position={[0, centerY, 0.6]} visible={false}>
        <circleGeometry args={[1, 32]} />
        <meshBasicMaterial color={FIRE_COLORS.coreWhite} transparent opacity={0} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
    </>
  );
};

// --- FIRE SHOCKWAVE: Expanding rings with thickness variation ---
const FireShockwave = ({ centerY, delay, ringIndex }) => {
  const ringRef = useRef();
  const glowRef = useRef();
  const startTime = useRef(null);

  const ringColor = useMemo(() => {
    const colors = [FIRE_COLORS.coreWhite, FIRE_COLORS.flameCore, FIRE_COLORS.flame, FIRE_COLORS.ember, FIRE_COLORS.lava];
    return colors[ringIndex % colors.length];
  }, [ringIndex]);

  useFrame((state) => {
    if (!ringRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 1.2) {
      ringRef.current.visible = false;
      if (glowRef.current) glowRef.current.visible = false;
      return;
    }

    ringRef.current.visible = true;
    if (glowRef.current) glowRef.current.visible = true;

    const progress = adjustedTime / 1.2;
    const easedScale = easeOutExpo(progress);

    const scale = easedScale * 28;
    ringRef.current.scale.setScalar(scale);

    const fadeIn = Math.min(progress * 12, 1);
    const fadeOut = 1 - smoothstep(progress);
    const flicker = Math.sin(elapsed * 35 + ringIndex * 3) * 0.12 + 0.88;
    ringRef.current.material.opacity = 0.7 * fadeIn * fadeOut * flicker;

    if (glowRef.current) {
      glowRef.current.scale.setScalar(scale * 0.88);
      glowRef.current.material.opacity = 0.4 * fadeIn * fadeOut * flicker;
    }
  });

  return (
    <>
      <mesh ref={ringRef} position={[0, centerY, 0.35]} visible={false}>
        <ringGeometry args={[0.88, 1.0, 48]} />
        <meshBasicMaterial color={ringColor} transparent opacity={0.7} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh ref={glowRef} position={[0, centerY, 0.33]} visible={false}>
        <ringGeometry args={[0.65, 0.92, 48]} />
        <meshBasicMaterial color={FIRE_COLORS.flameCore} transparent opacity={0.4} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
    </>
  );
};

// --- FIRE VORTEX PARTICLE: Individual particle spinning in a vortex ---
const FireVortexParticle = ({ centerY, index, totalCount }) => {
  const meshRef = useRef();
  const startTime = useRef(null);

  const config = useMemo(() => {
    const angle = (index / totalCount) * Math.PI * 2;
    const colors = [FIRE_COLORS.flameCore, FIRE_COLORS.flame, FIRE_COLORS.ember, FIRE_COLORS.spark, FIRE_COLORS.coreWhite];
    return {
      baseAngle: angle,
      radius: 0.5 + Math.random() * 0.8,
      speed: 8 + Math.random() * 6,
      riseSpeed: 2 + Math.random() * 3,
      size: 0.06 + Math.random() * 0.08,
      color: colors[Math.floor(Math.random() * colors.length)],
      flickerSpeed: 20 + Math.random() * 30,
      flickerOffset: Math.random() * Math.PI * 2,
      lifetime: 1.2 + Math.random() * 0.6,
    };
  }, [index, totalCount]);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - 0.3;

    if (adjustedTime <= 0 || adjustedTime > config.lifetime) {
      meshRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const progress = adjustedTime / config.lifetime;

    // Spiral outward while rising
    const expandProgress = easeOutCubic(progress);
    const currentRadius = config.radius + expandProgress * 4;
    const currentAngle = config.baseAngle + adjustedTime * config.speed;

    meshRef.current.position.x = Math.cos(currentAngle) * currentRadius;
    meshRef.current.position.y = centerY + adjustedTime * config.riseSpeed * (1 - progress * 0.3);
    meshRef.current.position.z = 0.4 + Math.sin(currentAngle * 2) * 0.15;

    // Flickering opacity
    const flicker = Math.sin(adjustedTime * config.flickerSpeed + config.flickerOffset) * 0.3 + 0.7;
    const fadeIn = Math.min(progress * 8, 1);
    const fadeOut = progress > 0.5 ? 1 - easeOutCubic((progress - 0.5) / 0.5) : 1;

    meshRef.current.material.opacity = flicker * fadeIn * fadeOut * 0.9;

    // Shrink as it dies
    const scale = config.size * (1 - progress * 0.5);
    meshRef.current.scale.setScalar(Math.max(0.01, scale));
  });

  return (
    <mesh ref={meshRef} visible={false}>
      <circleGeometry args={[0.08, 6]} />
      <meshBasicMaterial
        color={config.color}
        transparent
        opacity={0.9}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </mesh>
  );
};

// --- FIRE PILLAR: Vertical columns of fire erupting from the rows ---
const FirePillar = ({ x, centerY, delay, direction }) => {
  const pillarRef = useRef();
  const glowRef = useRef();
  const coreRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!pillarRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 1.1) {
      pillarRef.current.visible = false;
      if (glowRef.current) glowRef.current.visible = false;
      if (coreRef.current) coreRef.current.visible = false;
      return;
    }

    pillarRef.current.visible = true;
    if (glowRef.current) glowRef.current.visible = true;
    if (coreRef.current) coreRef.current.visible = true;

    const progress = adjustedTime / 1.1;
    const flicker = Math.sin(elapsed * 40 + x * 5) * 0.2 + 0.8;

    if (progress < 0.25) {
      const growProgress = progress / 0.25;
      const eased = easeOutExpo(growProgress);
      const height = eased * 10;
      pillarRef.current.scale.set(1, height, 1);
      pillarRef.current.position.y = centerY + direction * height * 0.5;
      pillarRef.current.material.opacity = 0.75 * eased * flicker;

      if (glowRef.current) {
        glowRef.current.scale.set(2.5, height * 1.3, 1);
        glowRef.current.position.y = pillarRef.current.position.y;
        glowRef.current.material.opacity = 0.35 * eased * flicker;
      }
      if (coreRef.current) {
        coreRef.current.scale.set(0.4, height * 0.8, 1);
        coreRef.current.position.y = pillarRef.current.position.y;
        coreRef.current.material.opacity = 0.5 * eased * flicker;
      }
    } else {
      const fadeProgress = (progress - 0.25) / 0.75;
      const eased = easeOutCubic(fadeProgress);
      const height = 10 + eased * 3;
      pillarRef.current.scale.set(1 - eased * 0.6, height, 1);
      pillarRef.current.position.y = centerY + direction * height * 0.5 + direction * eased * 3;
      pillarRef.current.material.opacity = 0.75 * (1 - eased) * flicker;

      if (glowRef.current) {
        glowRef.current.scale.set(2.5 - eased * 1.5, height * 1.3, 1);
        glowRef.current.position.y = pillarRef.current.position.y;
        glowRef.current.material.opacity = 0.35 * (1 - eased) * flicker;
      }
      if (coreRef.current) {
        coreRef.current.scale.set(Math.max(0.05, 0.4 - eased * 0.35), height * 0.8, 1);
        coreRef.current.position.y = pillarRef.current.position.y;
        coreRef.current.material.opacity = 0.5 * (1 - eased) * flicker;
      }
    }
  });

  return (
    <>
      <mesh ref={pillarRef} position={[x, centerY, 0.3]} visible={false}>
        <planeGeometry args={[0.7, 1]} />
        <meshBasicMaterial color={FIRE_COLORS.flame} transparent opacity={0.75} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
      <mesh ref={glowRef} position={[x, centerY, 0.25]} visible={false}>
        <planeGeometry args={[1.4, 1]} />
        <meshBasicMaterial color={FIRE_COLORS.ember} transparent opacity={0.35} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
      {/* White-hot core of pillar */}
      <mesh ref={coreRef} position={[x, centerY, 0.35]} visible={false}>
        <planeGeometry args={[0.3, 1]} />
        <meshBasicMaterial color={FIRE_COLORS.coreWhite} transparent opacity={0.5} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
    </>
  );
};

// --- RADIAL FIRE LINES: Starburst fire rays ---
const RadialFireLine = ({ centerY, angle, delay }) => {
  const lineRef = useRef();
  const coreRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!lineRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 0.8) {
      lineRef.current.visible = false;
      if (coreRef.current) coreRef.current.visible = false;
      return;
    }

    lineRef.current.visible = true;
    if (coreRef.current) coreRef.current.visible = true;

    const progress = adjustedTime / 0.8;
    const eased = easeOutExpo(progress);
    const flicker = Math.sin(elapsed * 45 + angle * 10) * 0.2 + 0.8;

    const length = eased * 16;
    lineRef.current.scale.set(0.14 + (1 - progress) * 0.18, length, 1);
    lineRef.current.position.x = Math.cos(angle) * length * 0.5;
    lineRef.current.position.y = centerY + Math.sin(angle) * length * 0.5;

    const fadeIn = Math.min(progress * 10, 1);
    const fadeOut = progress > 0.35 ? 1 - easeOutCubic((progress - 0.35) / 0.65) : 1;
    lineRef.current.material.opacity = 0.85 * fadeIn * fadeOut * flicker;

    // Bright core line
    if (coreRef.current) {
      coreRef.current.scale.set(0.06 + (1 - progress) * 0.08, length * 0.9, 1);
      coreRef.current.position.x = lineRef.current.position.x;
      coreRef.current.position.y = lineRef.current.position.y;
      coreRef.current.material.opacity = 0.6 * fadeIn * fadeOut * flicker;
    }
  });

  return (
    <>
      <mesh ref={lineRef} position={[0, centerY, 0.38]} rotation={[0, 0, angle - Math.PI / 2]} visible={false}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial color={FIRE_COLORS.flame} transparent opacity={0.85} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
      <mesh ref={coreRef} position={[0, centerY, 0.4]} rotation={[0, 0, angle - Math.PI / 2]} visible={false}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial color={FIRE_COLORS.coreWhite} transparent opacity={0.6} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
    </>
  );
};

// --- ERUPTION SPARK: Molten sparks that arc outward with gravity ---
const EruptionSpark = ({ startPosition, velocity, delay, color, life }) => {
  const meshRef = useRef();
  const trailRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > life) {
      meshRef.current.visible = false;
      if (trailRef.current) trailRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const progress = adjustedTime / life;
    const gravity = -14;

    const t = adjustedTime;
    const x = startPosition[0] + velocity[0] * t;
    const y = startPosition[1] + velocity[1] * t + 0.5 * gravity * t * t;
    const z = startPosition[2];

    meshRef.current.position.set(x, y, z);

    const flicker = Math.sin(adjustedTime * 50 + startPosition[0] * 8) * 0.35 + 0.65;

    const fadeIn = Math.min(progress * 10, 1);
    const fadeOut = progress > 0.3 ? 1 - easeOutCubic((progress - 0.3) / 0.7) : 1;
    const opacity = flicker * fadeIn * fadeOut;

    meshRef.current.material.opacity = opacity;

    const scale = 0.14 * (1 - progress * 0.5);
    meshRef.current.scale.setScalar(Math.max(0.01, scale));

    if (trailRef.current) {
      trailRef.current.visible = progress < 0.75;
      const trailT = Math.max(0, t - 0.025);
      const trailX = startPosition[0] + velocity[0] * trailT;
      const trailY = startPosition[1] + velocity[1] * trailT + 0.5 * gravity * trailT * trailT;
      trailRef.current.position.set(trailX, trailY, z - 0.02);
      trailRef.current.material.opacity = opacity * 0.45;
      trailRef.current.scale.setScalar(scale * 2);
    }
  });

  return (
    <>
      <mesh ref={meshRef} position={startPosition} visible={false}>
        <circleGeometry args={[0.1, 6]} />
        <meshBasicMaterial color={color} transparent opacity={0.9} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
      <mesh ref={trailRef} position={startPosition} visible={false}>
        <circleGeometry args={[0.1, 6]} />
        <meshBasicMaterial color={FIRE_COLORS.ember} transparent opacity={0.45} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
    </>
  );
};

// --- FALLING FIRE TETROMINO: Mini tetromino shapes made of fire raining down ---
const FallingFireTetromino = ({ startPosition, delay, shapeIndex }) => {
  const groupRef = useRef();
  const blockRefs = useRef([]);
  const startTime = useRef(null);

  const config = useMemo(() => {
    const shape = TETROMINO_SHAPES[shapeIndex % TETROMINO_SHAPES.length];
    const colors = [FIRE_COLORS.flameCore, FIRE_COLORS.flame, FIRE_COLORS.ember, FIRE_COLORS.spark];
    const blockSize = 0.12 + Math.random() * 0.06;
    return {
      shape,
      color: colors[Math.floor(Math.random() * colors.length)],
      blockSize,
      fallSpeed: 2.5 + Math.random() * 3,
      drift: (Math.random() - 0.5) * 2,
      wobbleSpeed: 1.5 + Math.random() * 3,
      wobbleAmp: 0.15 + Math.random() * 0.25,
      spin: (Math.random() - 0.5) * 3,
      flickerSpeed: 12 + Math.random() * 20,
      flickerOffset: Math.random() * Math.PI * 2,
      lifetime: 1.8 + Math.random() * 1.0,
    };
  }, [shapeIndex]);

  useFrame((state) => {
    if (!groupRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > config.lifetime) {
      groupRef.current.visible = false;
      return;
    }

    groupRef.current.visible = true;
    const progress = adjustedTime / config.lifetime;

    // Gentle falling with wobble
    const wobble = Math.sin(adjustedTime * config.wobbleSpeed + config.flickerOffset) * config.wobbleAmp;
    groupRef.current.position.x = startPosition[0] + wobble + config.drift * progress;
    groupRef.current.position.y = startPosition[1] - adjustedTime * config.fallSpeed;
    groupRef.current.position.z = startPosition[2];

    // Slow tumble rotation
    groupRef.current.rotation.z += config.spin * 0.012;

    // Flicker
    const flicker = Math.sin(adjustedTime * config.flickerSpeed + config.flickerOffset) * 0.25 + 0.75;
    const fadeIn = Math.min(progress * 5, 1);
    const fadeOut = progress > 0.6 ? 1 - easeOutCubic((progress - 0.6) / 0.4) : 1;
    const opacity = flicker * fadeIn * fadeOut * 0.85;

    // Update each mini-block
    blockRefs.current.forEach((ref) => {
      if (ref && ref.material) {
        ref.material.opacity = opacity;
      }
    });
  });

  return (
    <group ref={groupRef} visible={false}>
      {config.shape.map(([cx, cy], i) => (
        <mesh
          key={i}
          ref={(el) => { blockRefs.current[i] = el; }}
          position={[cx * config.blockSize * 1.1, cy * config.blockSize * 1.1, 0]}
        >
          <boxGeometry args={[config.blockSize, config.blockSize, config.blockSize * 0.5]} />
          <meshBasicMaterial
            color={config.color}
            transparent
            opacity={0.85}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
};

// --- EMBER RAIN: Glowing embers drifting down after the explosion ---
const EmberRainParticle = ({ startPosition, delay }) => {
  const meshRef = useRef();
  const glowRef = useRef();
  const startTime = useRef(null);

  const drift = useMemo(() => ({
    x: (Math.random() - 0.5) * 1.8,
    fallSpeed: 1.0 + Math.random() * 1.8,
    wobbleSpeed: 2 + Math.random() * 3,
    wobbleAmp: 0.2 + Math.random() * 0.5,
    flickerOffset: Math.random() * Math.PI * 2,
    flickerSpeed: 15 + Math.random() * 25,
    size: 0.04 + Math.random() * 0.07,
    spin: (Math.random() - 0.5) * 5,
  }), []);

  const color = useMemo(() => {
    const colors = [FIRE_COLORS.flameCore, FIRE_COLORS.flame, FIRE_COLORS.ember, FIRE_COLORS.spark, FIRE_COLORS.coreWhite];
    return colors[Math.floor(Math.random() * colors.length)];
  }, []);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 2.8) {
      meshRef.current.visible = false;
      if (glowRef.current) glowRef.current.visible = false;
      return;
    }

    meshRef.current.visible = true;

    const progress = adjustedTime / 2.8;

    const wobble = Math.sin(adjustedTime * drift.wobbleSpeed + drift.flickerOffset) * drift.wobbleAmp;
    meshRef.current.position.x = startPosition[0] + wobble + drift.x * progress;
    meshRef.current.position.y = startPosition[1] - adjustedTime * drift.fallSpeed;
    meshRef.current.position.z = startPosition[2];

    meshRef.current.rotation.z += drift.spin * 0.008;

    const flicker1 = Math.sin(adjustedTime * drift.flickerSpeed + drift.flickerOffset) * 0.3;
    const flicker2 = Math.sin(adjustedTime * drift.flickerSpeed * 0.6 + 2) * 0.15;
    const sparkle = 0.55 + flicker1 + flicker2;

    const fadeIn = Math.min(progress * 6, 1);
    const fadeOut = progress > 0.45 ? 1 - easeOutCubic((progress - 0.45) / 0.55) : 1;

    meshRef.current.material.opacity = sparkle * fadeIn * fadeOut * 0.9;

    const scale = drift.size + sparkle * 0.03;
    meshRef.current.scale.setScalar(Math.max(0.01, scale));

    // Soft glow halo around each ember
    if (glowRef.current) {
      glowRef.current.visible = meshRef.current.visible;
      glowRef.current.position.copy(meshRef.current.position);
      glowRef.current.position.z -= 0.02;
      glowRef.current.material.opacity = meshRef.current.material.opacity * 0.3;
      glowRef.current.scale.setScalar(scale * 3);
    }
  });

  return (
    <>
      <mesh ref={meshRef} position={startPosition} visible={false}>
        <circleGeometry args={[0.06, 5]} />
        <meshBasicMaterial color={color} transparent opacity={0.9} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
      <mesh ref={glowRef} position={startPosition} visible={false}>
        <circleGeometry args={[0.06, 8]} />
        <meshBasicMaterial color={FIRE_COLORS.ember} transparent opacity={0.3} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
    </>
  );
};

// --- HEAT WAVE: Ripple sweeping across rows ---
const HeatWave = ({ row, delay }) => {
  const waveRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!waveRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 0.6) {
      waveRef.current.visible = false;
      return;
    }

    waveRef.current.visible = true;

    const progress = adjustedTime / 0.6;
    const eased = easeOutCubic(progress);

    const xPos = -6 + eased * 12;
    waveRef.current.position.x = xPos;

    const intensity = Math.sin(progress * Math.PI);
    waveRef.current.material.opacity = 0.55 * intensity;
    waveRef.current.scale.set(1.5 + intensity * 2.5, 1.3 + intensity * 0.6, 1);
  });

  const yPos = 20 - row - 0.5;

  return (
    <mesh ref={waveRef} position={[0, yPos, 0.28]} visible={false}>
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial color={FIRE_COLORS.heatDistort} transparent opacity={0.55} blending={THREE.AdditiveBlending} depthWrite={false} />
    </mesh>
  );
};

// --- IGNITION FLASH: White-hot row flash ---
const IgnitionFlash = ({ row, delay }) => {
  const flashRef = useRef();
  const startTime = useRef(null);

  useFrame((state) => {
    if (!flashRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > 0.22) {
      flashRef.current.visible = false;
      return;
    }

    flashRef.current.visible = true;

    const progress = adjustedTime / 0.22;
    const intensity = Math.sin(progress * Math.PI);
    flashRef.current.material.opacity = 0.95 * intensity;
    flashRef.current.scale.set(12 + progress * 2, 1.4 + intensity * 0.4, 1);
  });

  const yPos = 20 - row - 0.5;

  return (
    <mesh ref={flashRef} position={[0, yPos, 0.42]} visible={false}>
      <planeGeometry args={[11, 1.2]} />
      <meshBasicMaterial color={FIRE_COLORS.coreWhite} transparent opacity={0.95} blending={THREE.AdditiveBlending} depthWrite={false} />
    </mesh>
  );
};

// --- LAVA DRIP: Molten lava dripping from the cleared row position ---
const LavaDrip = ({ x, startY, delay }) => {
  const dripRef = useRef();
  const glowRef = useRef();
  const startTime = useRef(null);

  const config = useMemo(() => ({
    speed: 3 + Math.random() * 4,
    wobble: (Math.random() - 0.5) * 0.3,
    size: 0.08 + Math.random() * 0.06,
    lifetime: 0.8 + Math.random() * 0.6,
  }), []);

  useFrame((state) => {
    if (!dripRef.current) return;

    if (startTime.current === null) {
      startTime.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTime.current;
    const adjustedTime = elapsed - delay;

    if (adjustedTime <= 0 || adjustedTime > config.lifetime) {
      dripRef.current.visible = false;
      if (glowRef.current) glowRef.current.visible = false;
      return;
    }

    dripRef.current.visible = true;

    const progress = adjustedTime / config.lifetime;

    // Drip downward with acceleration
    const fallDist = adjustedTime * config.speed + 0.5 * 6 * adjustedTime * adjustedTime;
    dripRef.current.position.x = x + Math.sin(adjustedTime * 8) * config.wobble;
    dripRef.current.position.y = startY - fallDist;
    dripRef.current.position.z = 0.3;

    // Stretch vertically as it falls (lava drip effect)
    const stretch = 1 + progress * 2;
    dripRef.current.scale.set(config.size, config.size * stretch, 1);

    const fadeOut = 1 - easeInQuad(progress);
    const flicker = Math.sin(adjustedTime * 30 + x * 5) * 0.2 + 0.8;
    dripRef.current.material.opacity = 0.8 * fadeOut * flicker;

    if (glowRef.current) {
      glowRef.current.visible = true;
      glowRef.current.position.copy(dripRef.current.position);
      glowRef.current.position.z -= 0.02;
      glowRef.current.scale.set(config.size * 3, config.size * stretch * 2, 1);
      glowRef.current.material.opacity = 0.35 * fadeOut * flicker;
    }
  });

  return (
    <>
      <mesh ref={dripRef} visible={false}>
        <circleGeometry args={[0.1, 6]} />
        <meshBasicMaterial color={FIRE_COLORS.flameCore} transparent opacity={0.8} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
      <mesh ref={glowRef} visible={false}>
        <circleGeometry args={[0.1, 6]} />
        <meshBasicMaterial color={FIRE_COLORS.lava} transparent opacity={0.35} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
    </>
  );
};


const EmberTetrisFinisher = ({ onComplete, blocks = [] }) => {
  const startTime = useRef(Date.now());

  const uniqueRows = useMemo(() => {
    if (blocks.length === 0) return [16, 17, 18, 19];
    const rows = [...new Set(blocks.map(b => b.row))];
    return rows.sort((a, b) => a - b);
  }, [blocks]);

  const centerY = useMemo(() => {
    const avgRow = uniqueRows.reduce((a, b) => a + b, 0) / uniqueRows.length;
    return 20 - avgRow - 0.5;
  }, [uniqueRows]);

  // Molten shatter blocks
  const shatterBlocks = useMemo(() => {
    const filledBlocksMap = new Map();
    blocks.forEach(block => {
      filledBlocksMap.set(`${block.row}-${block.col}`, block.colorClass);
    });

    const allBlocks = [];
    uniqueRows.forEach((row) => {
      for (let col = 0; col < 10; col++) {
        const key = `${row}-${col}`;
        const colorClass = filledBlocksMap.get(key) || 'l-tetromino-active';
        allBlocks.push({ row, col, colorClass });
      }
    });

    return allBlocks.map((block) => {
      const x = block.col - 4.5;
      const y = 20 - block.row - 0.5;
      const color = getBlockColor(block.colorClass);

      const distFromCenter = Math.sqrt(x * x + (y - centerY) * (y - centerY));
      const delay = distFromCenter * 0.035;

      return {
        position: [x, y, 0.2],
        color,
        delay,
        col: block.col,
        row: block.row,
        centerX: 0,
        centerY,
      };
    });
  }, [blocks, uniqueRows, centerY]);

  // Fire shockwave rings — 5 rings with staggered timing
  const shockwaveRings = useMemo(() => {
    return [0, 1, 2, 3, 4].map(i => ({
      delay: 0.28 + i * 0.08,
      ringIndex: i,
    }));
  }, []);

  // Fire vortex particles
  const vortexParticles = useMemo(() => {
    const count = 30;
    return Array.from({ length: count }, (_, i) => ({ index: i, totalCount: count }));
  }, []);

  // Fire pillars
  const firePillars = useMemo(() => {
    const pillars = [];
    for (let i = 0; i < 7; i++) {
      const x = (i / 6) * 10 - 5;
      pillars.push({ x, delay: 0.32 + Math.abs(x) * 0.025, direction: 1 });
      pillars.push({ x, delay: 0.35 + Math.abs(x) * 0.025, direction: -1 });
    }
    return pillars;
  }, []);

  // Radial fire lines — 20 for denser starburst
  const radialLines = useMemo(() => {
    const lines = [];
    const count = 20;
    for (let i = 0; i < count; i++) {
      lines.push({
        angle: (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.12,
        delay: 0.3 + Math.random() * 0.06,
      });
    }
    return lines;
  }, []);

  // Eruption sparks — 4 per block for denser particle field
  const eruptionSparks = useMemo(() => {
    const sparks = [];
    const sourceBlocks = blocks.length > 0 ? blocks : (() => {
      const fallback = [];
      for (let row = 16; row < 20; row++) {
        for (let col = 0; col < 10; col++) {
          fallback.push({ row, col });
        }
      }
      return fallback;
    })();

    sourceBlocks.forEach((block) => {
      const baseX = block.col - 4.5;
      const baseY = 20 - block.row - 0.5;

      for (let s = 0; s < 4; s++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 3.5 + Math.random() * 6;
        const upBias = 7 + Math.random() * 10;

        const colors = [FIRE_COLORS.flameCore, FIRE_COLORS.flame, FIRE_COLORS.ember, FIRE_COLORS.spark, FIRE_COLORS.coreWhite];

        sparks.push({
          startPosition: [baseX + (Math.random() - 0.5) * 0.3, baseY, 0.35],
          velocity: [
            Math.cos(angle) * speed + baseX * 0.9,
            upBias,
            0,
          ],
          delay: 0.33 + Math.random() * 0.15,
          color: colors[Math.floor(Math.random() * colors.length)],
          life: 0.7 + Math.random() * 0.9,
        });
      }
    });

    return sparks;
  }, [blocks]);

  // Falling fire tetrominos — the signature effect
  const fallingTetrominos = useMemo(() => {
    const tetrominos = [];
    const count = 20;

    for (let i = 0; i < count; i++) {
      const x = (Math.random() - 0.5) * 14;
      const y = centerY + 3 + Math.random() * 6;

      tetrominos.push({
        startPosition: [x, y, 0.2 + Math.random() * 0.1],
        delay: 0.6 + Math.random() * 1.0,
        shapeIndex: Math.floor(Math.random() * TETROMINO_SHAPES.length),
      });
    }

    return tetrominos;
  }, [centerY]);

  // Ember rain
  const emberRain = useMemo(() => {
    const particles = [];
    const count = 60;

    for (let i = 0; i < count; i++) {
      const x = (Math.random() - 0.5) * 15;
      const y = centerY + (Math.random() - 0.2) * 6 + 5;

      particles.push({
        startPosition: [x, y, 0.15 + Math.random() * 0.1],
        delay: 0.7 + Math.random() * 0.9,
      });
    }

    return particles;
  }, [centerY]);

  // Lava drips from cleared row edges
  const lavaDrips = useMemo(() => {
    const drips = [];
    uniqueRows.forEach((row) => {
      const y = 20 - row - 0.5;
      for (let i = 0; i < 8; i++) {
        const x = (Math.random() - 0.5) * 10;
        drips.push({
          x,
          startY: y,
          delay: 0.4 + Math.random() * 0.3,
        });
      }
    });
    return drips;
  }, [uniqueRows]);

  // Heat waves
  const heatWaves = useMemo(() => {
    return uniqueRows.map((row, i) => ({
      row,
      delay: 0.08 + i * 0.05,
    }));
  }, [uniqueRows]);

  // Ignition flashes
  const ignitionFlashes = useMemo(() => {
    return uniqueRows.map((row, i) => ({
      row,
      delay: 0.25 + i * 0.035,
    }));
  }, [uniqueRows]);

  useFrame(() => {
    const elapsed = (Date.now() - startTime.current) / 1000;
    if (elapsed > 3.0) {
      onComplete?.();
    }
  });

  return (
    <group>
      {/* Phase 1: Heat waves sweep across rows */}
      {heatWaves.map((wave, i) => (
        <HeatWave key={`wave-${i}`} row={wave.row} delay={wave.delay} />
      ))}

      {/* Phase 2: Ignition flash on each row */}
      {ignitionFlashes.map((flash, i) => (
        <IgnitionFlash key={`flash-${i}`} row={flash.row} delay={flash.delay} />
      ))}

      {/* Phase 2: Volcanic core explosion — multi-layered */}
      <VolcanicCore centerY={centerY} />

      {/* Phase 2: Fire shockwave rings */}
      {shockwaveRings.map((ring, i) => (
        <FireShockwave
          key={`shock-${i}`}
          centerY={centerY}
          delay={ring.delay}
          ringIndex={ring.ringIndex}
        />
      ))}

      {/* Phase 2: Radial fire starburst lines */}
      {radialLines.map((line, i) => (
        <RadialFireLine
          key={`radial-${i}`}
          centerY={centerY}
          angle={line.angle}
          delay={line.delay}
        />
      ))}

      {/* Phase 2: Fire pillars erupting */}
      {firePillars.map((pillar, i) => (
        <FirePillar
          key={`pillar-${i}`}
          x={pillar.x}
          centerY={centerY}
          delay={pillar.delay}
          direction={pillar.direction}
        />
      ))}

      {/* Phase 2: Fire vortex — spinning tornado of embers */}
      {vortexParticles.map((p, i) => (
        <FireVortexParticle
          key={`vortex-${i}`}
          centerY={centerY}
          index={p.index}
          totalCount={p.totalCount}
        />
      ))}

      {/* Phase 1-3: Molten shatter blocks — heat up, crack, shatter into fragments */}
      {shatterBlocks.map((block, i) => (
        <MoltenShatterBlock
          key={`shatter-${i}`}
          position={block.position}
          color={block.color}
          delay={block.delay}
          col={block.col}
          row={block.row}
          centerX={block.centerX}
          centerY={block.centerY}
        />
      ))}

      {/* Phase 2: Lava drips from cleared rows */}
      {lavaDrips.map((drip, i) => (
        <LavaDrip
          key={`drip-${i}`}
          x={drip.x}
          startY={drip.startY}
          delay={drip.delay}
        />
      ))}

      {/* Phase 2-3: Eruption sparks arcing outward */}
      {eruptionSparks.map((spark, i) => (
        <EruptionSpark
          key={`spark-${i}`}
          startPosition={spark.startPosition}
          velocity={spark.velocity}
          delay={spark.delay}
          color={spark.color}
          life={spark.life}
        />
      ))}

      {/* Phase 3: Falling fire tetrominos — blocks become mini tetrominos raining down */}
      {fallingTetrominos.map((tetro, i) => (
        <FallingFireTetromino
          key={`tetro-${i}`}
          startPosition={tetro.startPosition}
          delay={tetro.delay}
          shapeIndex={tetro.shapeIndex}
        />
      ))}

      {/* Phase 3: Ember rain — lingering glowing particles */}
      {emberRain.map((ember, i) => (
        <EmberRainParticle
          key={`ember-${i}`}
          startPosition={ember.startPosition}
          delay={ember.delay}
        />
      ))}
    </group>
  );
};

export default EmberTetrisFinisher;
